import React from 'react';
import { UnscheduledEvent } from '../types';
import { extractDate, extractTime } from './eventModal/dateTimeUtils';
import LabeledDateTimeRow from './LabeledDateTimeRow';

/**
 * Props for the audit read-only event summary.
 */
interface AuditEventSummaryProps {
  event: UnscheduledEvent;
}

/**
 * The read-only header of the Audit popup: aircraft, Time Declared, and the
 * previous/next flight numbers and times the audit calculations subtract.
 *
 * Next Flight Date & Time mirrors the Chargeable flight, so it reads the frozen
 * Chargeable STD rather than `nextScheduledFlightTimeZulu` (which tracks the
 * live Next Scheduled flight and can drift to a later flight). It falls back to
 * `nextScheduledFlightTimeZulu` for older events saved before the Chargeable STD
 * was captured.
 */
const AuditEventSummary: React.FC<AuditEventSummaryProps> = ({ event }) => {
  const chargeableStd =
    event.chargeableFlightScheduledDepartureTimeZulu || event.nextScheduledFlightTimeZulu;
  const previousFlightZulu = event.previousFlightScheduledDepartureTimeZulu || '';
  return (
    <>
      <div className="field-row">
        <label className="field-label">Aircraft</label>
        <div className="field-group">
          <input type="text" value={event.tailNumber} readOnly className="field-input" />
        </div>
      </div>

      {/* Time Declared is rendered as separate Date and Time (Z) fields —
          matching the Previous/Next Flight rows — so the values line up with
          the other date/time fields the audit calculations subtract. */}
      <LabeledDateTimeRow
        label="Time Declared"
        date={extractDate(event.timeDeclaredZulu || '')}
        time={extractTime(event.timeDeclaredZulu || '')}
      />

      <div className="field-row">
        <label className="field-label">Previous Flight Number</label>
        <div className="field-group">
          <input
            type="text"
            value={event.inboundFlightNumber || '—'}
            readOnly
            className="field-input"
          />
        </div>
      </div>

      <LabeledDateTimeRow
        label="Previous Flight Date & Time"
        date={extractDate(previousFlightZulu)}
        time={extractTime(previousFlightZulu)}
      />

      <div className="field-row">
        <label className="field-label">Next Flight Number</label>
        <div className="field-group">
          <input
            type="text"
            value={event.outboundFlightNumber || '—'}
            readOnly
            className="field-input"
          />
        </div>
      </div>

      <LabeledDateTimeRow
        label="Next Flight Date & Time"
        date={extractDate(chargeableStd)}
        time={extractTime(chargeableStd)}
      />
    </>
  );
};

export default AuditEventSummary;
